import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { INVOICE_TONES, useAdminInvoices } from '@/hooks/invoices';
import { formatDay } from '@/lib/dates';
import { NewInvoiceDialog } from '@/pages/admin/invoices/InvoiceDialogs';
import { formatCents } from '@shared/money';
import type { AdminInvoiceListItem } from '@shared/schemas/invoice';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * One client's invoices, on their own detail page — the seam the full audit of
 * 2026-08-06 found open between phase 3.6 and phase 12.
 *
 * **Nothing new was built to serve this.** `invoiceListQuerySchema` has always
 * carried an optional `clientId` and `adminListFilters` has always applied it;
 * `NewInvoiceDialog` has always taken a `presetClientId` that hides the client
 * picker. Both existed from 12.2 and neither had a caller. This tab is the
 * caller.
 *
 * **The columns are /admin/invoices' columns minus the client**, because the
 * client is the page. Deliberately no total across the invoices: what a
 * customer is "worth" is a money figure with more than one defensible
 * definition (billed or collected, before or after voids), and inventing one
 * on a screen is how a number nobody agreed on ends up in front of an
 * accountant. It is written up for the owner instead.
 */
export function ClientInvoicesTab({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const invoices = useAdminInvoices({ sort: 'newest', page, status: 'all', clientId });

  const columns: Column<AdminInvoiceListItem>[] = [
    {
      key: 'number',
      header: 'Invoice',
      render: (row) => (
        <Link
          to={`/admin/invoices/${row.id}`}
          className="font-medium text-bark-700 underline underline-offset-2"
        >
          {row.invoiceNumber}
        </Link>
      ),
    },
    {
      key: 'issued',
      header: 'Issued',
      render: (row) =>
        row.issueDate === null ? (
          <span className="text-ink-500">Not yet</span>
        ) : (
          <span className="whitespace-nowrap">{formatDay(row.issueDate)}</span>
        ),
    },
    {
      key: 'due',
      header: 'Due',
      render: (row) => (
        <span className="whitespace-nowrap">
          {row.dueDate === null ? <span className="text-ink-500">—</span> : formatDay(row.dueDate)}
          {row.isOverdue ? (
            <span className="ml-2 rounded-sm bg-red-100 px-1.5 py-0.5 text-meta font-medium text-red-800">
              Overdue
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">{formatCents(row.totalCents)}</span>
      ),
    },
    {
      // Zero is $0.00 — a settled invoice's balance is a fact, not a dash (P1).
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">{formatCents(row.balanceCents)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge label={row.statusLabel} tone={INVOICE_TONES[row.status]} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" className="size-4" />
          New invoice
        </Button>
      </div>

      <DataTable
        caption={`Invoices for ${clientName}, newest first`}
        columns={columns}
        rows={invoices.data?.data ?? []}
        rowKey={(row) => row.id}
        // On a phone the whole card is the link — see DataTable's `rowHref`.
        rowHref={(row) => `/admin/invoices/${row.id}`}
        isLoading={invoices.isPending}
        // A failed request is an error, never an empty state — P22.
        error={invoices.error}
        onRetry={() => {
          void invoices.refetch();
        }}
        empty={
          <EmptyState
            icon={FileText}
            title="Nothing billed yet."
            hint="Build one from their completed cleans — each job becomes a line priced from the rate card."
          />
        }
      />

      <Pagination
        page={page}
        pageSize={invoices.data?.pageSize ?? DEFAULT_PAGE_SIZE}
        total={invoices.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="invoices"
      />

      <NewInvoiceDialog
        open={creating}
        onOpenChange={setCreating}
        presetClientId={clientId}
        onCreated={(id) => void navigate(`/admin/invoices/${id}`)}
      />
    </div>
  );
}
